import React from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import {
  TableCellsIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ArrowsRightLeftIcon,
  ExclamationTriangleIcon,
  ArrowRightIcon,
  LightBulbIcon,
  BoltIcon,
  CircleStackIcon
} from '@heroicons/react/24/outline';

export default function FieldsGuidePage() {
  const { orgId } = useParams();
  const basePath = orgId ? `/${orgId}` : '';

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <nav className="mb-8">
          <ol className="flex items-center space-x-2 text-sm">
            <li>
              <Link to={`${basePath}/help`} className="text-gray-500 hover:text-gray-700">
                Help Center
              </Link>
            </li>
            <li className="text-gray-500">/</li>
            <li className="text-gray-900">Fields Guide</li>
          </ol>
        </nav>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <TableCellsIcon className="h-8 w-8 text-blue-600 mr-3" />
            Field Impact Analysis
          </h1>
          <p className="mt-2 text-lg text-gray-600">
            Understand which flows use specific fields in your Salesforce objects, helping you make safe data model changes and identify field dependencies.
          </p>
        </div>

        {/* Overview Section */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Why Field Analysis Matters</h2>
          
          <div className="bg-white rounded-lg shadow-sm p-6">
            <p className="text-gray-700 mb-4">
              Before modifying or removing fields in Salesforce, you need to understand which automations depend on them. The Fields page shows exactly which flows reference each field and what operations they perform.
            </p>
            
            <div className="mt-4 grid md:grid-cols-2 gap-4">
              <div className="p-4 bg-blue-50 rounded-md">
                <h3 className="font-medium text-blue-900 mb-2">For Admins:</h3>
                <ul className="space-y-1 text-sm text-blue-700">
                  <li>• Safe field deprecation planning</li>
                  <li>• Impact analysis before changes</li>
                  <li>• Identify unused fields</li>
                  <li>• Understand data dependencies</li>
                </ul>
              </div>
              
              <div className="p-4 bg-purple-50 rounded-md">
                <h3 className="font-medium text-purple-900 mb-2">For Developers:</h3>
                <ul className="space-y-1 text-sm text-purple-700">
                  <li>• See field usage in flows</li>
                  <li>• Track CRUD operations</li>
                  <li>• Plan data model changes</li>
                  <li>• Prevent breaking changes</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">How the Fields Page Works</h2>
          
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-start">
                <TableCellsIcon className="h-6 w-6 text-blue-600 mt-1" />
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900">Three-Column View</h3>
                  <p className="mt-1 text-gray-600">
                    Information is organized in an easy-to-scan table format:
                  </p>
                  <div className="mt-3 space-y-3">
                    <div>
                      <h4 className="font-medium text-gray-900">Object Column</h4>
                      <p className="text-sm text-gray-600">Shows Salesforce objects (Account, Opportunity, custom objects) with cleaned names</p>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900">Field Column</h4>
                      <p className="text-sm text-gray-600">Lists all fields within each object that are referenced by flows</p>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900">Flows Column</h4>
                      <p className="text-sm text-gray-600">Shows which flows use each field with operation type badges</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-start">
                <CircleStackIcon className="h-6 w-6 text-purple-600 mt-1" />
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900">Operation Types</h3>
                  <p className="mt-1 text-gray-600">
                    Each flow shows what it does with the field:
                  </p>
                  <div className="mt-3 space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-medium rounded">create</span>
                      <span className="text-sm text-gray-600">Flow creates new records with this field</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-medium rounded">update</span>
                      <span className="text-sm text-gray-600">Flow modifies this field's value</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-medium rounded">delete</span>
                      <span className="text-sm text-gray-600">Flow deletes records</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs font-medium rounded">read</span>
                      <span className="text-sm text-gray-600">Flow only references the field</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Filtering Section */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Search and Filter Capabilities</h2>
          
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-start">
              <FunnelIcon className="h-6 w-6 text-blue-600 mt-1" />
              <div className="ml-4 flex-1">
                <h3 className="text-lg font-medium text-gray-900">Finding Field Dependencies</h3>
                <p className="mt-1 text-gray-600 mb-4">
                  Multiple ways to find the information you need:
                </p>
                
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium text-gray-900">Object Filter</h4>
                    <p className="text-sm text-gray-600 mt-1">
                      Select a specific object to see only its fields and related flows
                    </p>
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-900">Field Filter</h4>
                    <p className="text-sm text-gray-600 mt-1">
                      Choose a specific field to see all flows that reference it across objects
                    </p>
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-900">Search Box</h4>
                    <p className="text-sm text-gray-600 mt-1">
                      Search across object names, field names, and flow names simultaneously
                    </p>
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-900">Modification Filter</h4>
                    <p className="text-sm text-gray-600 mt-1">
                      Toggle "Show only fields modified by flows" to focus on fields with create/update/delete operations
                    </p>
                  </div>
                </div>

                <div className="mt-4 p-4 bg-green-50 rounded-md">
                  <p className="text-sm font-medium text-green-900">💡 Pro Tip:</p>
                  <p className="text-sm text-green-700 mt-1">
                    Use the modification filter to quickly identify fields that flows are actively changing vs. just reading.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Common Use Cases */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Common Use Cases</h2>
          
          <div className="space-y-4">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="font-medium text-gray-900 mb-3">🧹 Field Deprecation Planning</h3>
              <ol className="space-y-2 text-sm text-gray-600">
                <li>1. Search for the field you want to remove</li>
                <li>2. Review all flows that reference it</li>
                <li>3. Note which flows modify vs. read the field</li>
                <li>4. Update flows before removing the field</li>
              </ol>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="font-medium text-gray-900 mb-3">🔍 Impact Analysis</h3>
              <ol className="space-y-2 text-sm text-gray-600">
                <li>1. Select an object to see all its field dependencies</li>
                <li>2. Identify high-impact fields used by many flows</li>
                <li>3. Click flow names to review their logic</li>
                <li>4. Document dependencies before making changes</li>
              </ol>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="font-medium text-gray-900 mb-3">🎯 Find Unused Fields</h3>
              <ol className="space-y-2 text-sm text-gray-600">
                <li>1. Compare fields shown here with your full field list in Salesforce</li>
                <li>2. Fields not appearing have no flow dependencies</li>
                <li>3. Verify with reports and other tools before removal</li>
                <li>4. Start cleanup with truly unused fields</li>
              </ol>
            </div>
          </div>
        </section>

        {/* Navigation Section */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Interactive Features</h2>
          
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-start">
              <BoltIcon className="h-6 w-6 text-green-600 mt-1" />
              <div className="ml-4">
                <h3 className="text-lg font-medium text-gray-900 mb-3">Quick Navigation</h3>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li>• <strong>Click any flow name</strong> to navigate directly to its detail page</li>
                  <li>• <strong>Alternating row colors</strong> make it easy to scan objects</li>
                  <li>• <strong>Cleaned object names</strong> remove Salesforce suffixes for readability</li>
                  <li>• <strong>Operation badges</strong> use color coding (red for modifications, gray for reads)</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Important Notes */}
        <section className="mb-12">
          <div className="bg-yellow-50 rounded-lg p-6 border border-yellow-200">
            <div className="flex items-start">
              <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600 mt-1" />
              <div className="ml-4">
                <h3 className="text-lg font-medium text-yellow-900">Important Considerations</h3>
                <ul className="mt-3 space-y-2 text-sm text-yellow-700">
                  <li>• This page shows only flow dependencies - fields may also be used in reports, formulas, or Apex</li>
                  <li>• Always check multiple sources before removing fields</li>
                  <li>• Some standard fields may not appear if flows don't explicitly reference them</li>
                  <li>• Managed package fields will appear with their namespace prefix</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Best Practices */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Best Practices</h2>
          
          <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-lg p-6 border border-yellow-200">
            <div className="flex items-start">
              <LightBulbIcon className="h-6 w-6 text-yellow-600 mt-1" />
              <div className="ml-4">
                <h3 className="text-lg font-medium text-gray-900 mb-3">Field Management Tips</h3>
                <ol className="space-y-3 text-gray-700">
                  <li>
                    <strong>1. Regular Audits:</strong>
                    <p className="text-sm mt-1">Review field usage quarterly to identify cleanup opportunities</p>
                  </li>
                  <li>
                    <strong>2. Document Before Removing:</strong>
                    <p className="text-sm mt-1">Screenshot or export field dependencies before making changes</p>
                  </li>
                  <li>
                    <strong>3. Test in Sandbox:</strong>
                    <p className="text-sm mt-1">Always test field removal impact in a sandbox first</p>
                  </li>
                  <li>
                    <strong>4. Communicate Changes:</strong>
                    <p className="text-sm mt-1">Notify flow owners before modifying shared fields</p>
                  </li>
                  <li>
                    <strong>5. Phase Deprecation:</strong>
                    <p className="text-sm mt-1">Mark obsolete → Update flows → Hide from layouts → Delete</p>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </section>

        {/* Next Steps */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Next Steps</h2>
          
          <div className="grid gap-4">
            <Link
              to={`${basePath}/fields`}
              className="block p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow border border-gray-200"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <TableCellsIcon className="h-5 w-5 text-gray-600 mr-3" />
                  <div>
                    <h4 className="font-medium text-gray-900">Analyze Your Fields</h4>
                    <p className="text-sm text-gray-600">Start exploring field dependencies</p>
                  </div>
                </div>
                <ArrowRightIcon className="h-5 w-5 text-gray-400" />
              </div>
            </Link>

            <Link
              to={`${basePath}/flows`}
              className="block p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow border border-gray-200"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <BoltIcon className="h-5 w-5 text-gray-600 mr-3" />
                  <div>
                    <h4 className="font-medium text-gray-900">View Flows</h4>
                    <p className="text-sm text-gray-600">See detailed flow information</p>
                  </div>
                </div>
                <ArrowRightIcon className="h-5 w-5 text-gray-400" />
              </div>
            </Link>
          </div>
        </section>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-gray-200">
          <div className="flex justify-between items-center">
            <Link to={`${basePath}/help`} className="text-blue-600 hover:text-blue-800 flex items-center">
              <ArrowRightIcon className="h-4 w-4 mr-1 rotate-180" />
              Back to Help Center
            </Link>
            <Link to={`${basePath}/fields`} className="text-blue-600 hover:text-blue-800 flex items-center">
              View Your Fields
              <ArrowRightIcon className="h-4 w-4 ml-1" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}